import React from 'react';
import { EnhancedCharacter } from '../../utils/ClassComponentRegistry';
import SlotTracker from './SlotTracker';
import styles from './CombatStatusSection.module.css';

export interface CombatStatusSectionProps {
  enhancedCharacter: EnhancedCharacter;
}

/**
 * CombatStatusSection - Unified section for all combat resources and status
 *
 * Shows:
 * - HP slots with threshold markers
 * - Armor slots
 * - Hope tokens
 * - Stress slots
 * - Evasion value
 * - Threshold values (Major/Severe)
 *
 * All PRIMARY combat information in one unified section.
 */
export const CombatStatusSection: React.FC<CombatStatusSectionProps> = ({
  enhancedCharacter,
}) => {
  const { character, computedStats } = enhancedCharacter;

  if (!character || !computedStats) {
    return (
      <div className={styles.combatStatusSection}>
        <div className={styles.combatStatusSection__loading}>
          Loading combat status...
        </div>
      </div>
    );
  }

  // HP handlers
  const handleHPSlotClick = (index: number) => {
    if (enhancedCharacter.updateCurrentState) {
      const markedHP = character.currentState.hpMarked || [];
      const highestMarked = markedHP.length > 0 ? Math.max(...markedHP) : -1;

      if (index === highestMarked) {
        const newMarked = markedHP.filter((i) => i !== index);
        enhancedCharacter.updateCurrentState({ hpMarked: newMarked });
      } else {
        const newMarked = Array.from({ length: index + 1 }, (_, i) => i);
        enhancedCharacter.updateCurrentState({ hpMarked: newMarked });
      }
    }
  };

  const handleHPClear = () => {
    if (enhancedCharacter.updateCurrentState) {
      enhancedCharacter.updateCurrentState({ hpMarked: [] });
    }
  };

  // Armor handlers
  const handleArmorSlotClick = (index: number) => {
    if (enhancedCharacter.updateCurrentState) {
      const markedArmor = character.currentState.armorMarked || [];
      const highestMarked = markedArmor.length > 0 ? Math.max(...markedArmor) : -1;

      if (index === highestMarked) {
        const newMarked = markedArmor.filter((i) => i !== index);
        enhancedCharacter.updateCurrentState({ armorMarked: newMarked });
      } else {
        const newMarked = Array.from({ length: index + 1 }, (_, i) => i);
        enhancedCharacter.updateCurrentState({ armorMarked: newMarked });
      }
    }
  };

  const handleArmorClear = () => {
    if (enhancedCharacter.updateCurrentState) {
      enhancedCharacter.updateCurrentState({ armorMarked: [] });
    }
  };

  // Hope handlers
  const handleHopeSlotClick = (index: number) => {
    const clickedHope = index + 1;
    const currentHope = character.currentState.currentHope || 0;

    if (enhancedCharacter.updateCurrentState) {
      if (clickedHope === currentHope) {
        enhancedCharacter.updateCurrentState({
          currentHope: Math.max(0, currentHope - 1),
        });
      } else {
        enhancedCharacter.updateCurrentState({
          currentHope: Math.min(clickedHope, maxHope),
        });
      }
    }
  };

  const handleHopeClear = () => {
    if (enhancedCharacter.updateCurrentState) {
      enhancedCharacter.updateCurrentState({ currentHope: 0 });
    }
  };

  // Stress handlers
  const handleStressSlotClick = (index: number) => {
    if (enhancedCharacter.updateCurrentState) {
      const markedStress = character.currentState.stressMarked || [];
      const highestMarked = markedStress.length > 0 ? Math.max(...markedStress) : -1;

      if (index === highestMarked) {
        const newMarked = markedStress.filter((i) => i !== index);
        enhancedCharacter.updateCurrentState({ stressMarked: newMarked });
      } else {
        const newMarked = Array.from({ length: index + 1 }, (_, i) => i);
        enhancedCharacter.updateCurrentState({ stressMarked: newMarked });
      }
    }
  };

  const handleStressClear = () => {
    if (enhancedCharacter.updateCurrentState) {
      enhancedCharacter.updateCurrentState({ stressMarked: [] });
    }
  };

  // Resource values
  const currentHP = computedStats.hpSlots ?? character.baseStats.hpSlots ?? 0;
  const markedHP = character.currentState.hpMarked || [];
  const majorThreshold = computedStats.thresholds.major ?? 0;
  const severeThreshold = computedStats.thresholds.severe ?? 0;

  const currentArmor = computedStats.armor ?? 0;
  const markedArmor = character.currentState.armorMarked || [];

  const maxHope = computedStats.hope ?? 6;
  const currentHope = character.currentState.currentHope ?? 0;
  const hopeIndices = Array.from({ length: currentHope }, (_, i) => i);

  const maxStress = computedStats.stressSlots ?? character.baseStats.stressSlots ?? 0;
  const markedStress = character.currentState.stressMarked || [];

  const evasion = computedStats.evasion ?? 11;

  return (
    <section className={styles.combatStatusSection} aria-labelledby="combat-status-heading">
      <h2 id="combat-status-heading" className={styles.combatStatusSection__title}>
        Combat Status
      </h2>

      <div className={styles.combatStatusSection__content}>
        {/* HP Tracker */}
        <div className={styles.combatStatusSection__resource}>
          <SlotTracker
            type="HP"
            label="Hit Points"
            icon="💔"
            currentSlots={currentHP}
            maxSlots={currentHP}
            markedIndices={markedHP}
            onSlotClick={handleHPSlotClick}
            onClear={handleHPClear}
            showCount={false}
            showLabel={true}
            thresholdMarkers={[majorThreshold, severeThreshold]}
          />
        </div>

        {/* Armor Tracker */}
        <div className={styles.combatStatusSection__resource}>
          <SlotTracker
            type="ARMOR"
            label="Armor"
            icon="🛡️"
            currentSlots={currentArmor}
            maxSlots={12}
            markedIndices={markedArmor}
            onSlotClick={handleArmorSlotClick}
            onClear={handleArmorClear}
            showCount={false}
            showLabel={true}
          />
        </div>

        {/* Hope Tracker */}
        <div className={styles.combatStatusSection__resource}>
          <SlotTracker
            type="HOPE"
            label="Hope"
            icon="⭐"
            currentSlots={maxHope}
            maxSlots={maxHope}
            markedIndices={hopeIndices}
            onSlotClick={handleHopeSlotClick}
            onClear={handleHopeClear}
            showCount={false}
            showLabel={true}
          />
        </div>

        {/* Stress Tracker */}
        <div className={styles.combatStatusSection__resource}>
          <SlotTracker
            type="STRESS"
            label="Stress"
            icon="⚡"
            currentSlots={maxStress}
            maxSlots={maxStress}
            markedIndices={markedStress}
            onSlotClick={handleStressSlotClick}
            onClear={handleStressClear}
            showCount={false}
            showLabel={true}
          />
        </div>

        {/* Combat Stats - Evasion & Thresholds */}
        <div className={styles.combatStatusSection__stats}>
          <div className={styles.combatStatusSection__evasion}>
            <span className={styles.combatStatusSection__statLabel}>
              Evasion:
            </span>
            <span className={styles.combatStatusSection__statValue}>
              {evasion}
            </span>
          </div>

          <div className={styles.combatStatusSection__thresholds}>
            <div className={styles.combatStatusSection__threshold}>
              <span className={styles.combatStatusSection__statLabel}>
                Major:
              </span>
              <span className={styles.combatStatusSection__statValue}>
                {majorThreshold}
              </span>
            </div>
            <div className={styles.combatStatusSection__threshold}>
              <span className={styles.combatStatusSection__statLabel}>
                Severe:
              </span>
              <span className={styles.combatStatusSection__statValue}>
                {severeThreshold}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CombatStatusSection;
